import { useEffect, useState } from "react";
import { Activity, TrendingUp, TrendingDown, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";

/**
 * CaulPriceTickerBadge — small pill showing the live CAUL/XRP DEX price.
 *
 * Reuses GET /api/caul/price (public, cached, 60s background poll).
 * Refreshes locally every 30 seconds. When the oracle reports `stale: true`
 * (empty order book on mainnet) we surface that visually so users know the
 * number is the last known print, not a live tick.
 */
export default function CaulPriceTickerBadge({ className = "" }) {
  const [price, setPrice] = useState(null);
  const [error, setError] = useState(false);
  const [prevPrice, setPrevPrice] = useState(null);
  const [trend, setTrend] = useState("flat"); // "up" | "down" | "flat"

  useEffect(() => {
    let cancelled = false;
    const fetchPrice = async () => {
      try {
        const { data } = await api.get("/caul/price");
        if (cancelled) return;
        setError(false);
        setPrice((cur) => {
          if (cur?.quotePrice && data?.quotePrice) {
            if (data.quotePrice > cur.quotePrice) setTrend("up");
            else if (data.quotePrice < cur.quotePrice) setTrend("down");
            else setTrend("flat");
            setPrevPrice(cur.quotePrice);
          }
          return data;
        });
      } catch {
        if (!cancelled) setError(true);
      }
    };
    fetchPrice();
    const id = setInterval(fetchPrice, 30_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (error || !price?.quotePrice) {
    return (
      <div
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#D4B58C]/50 bg-[#D4B58C]/[0.06] text-[11px] ${className}`}
        style={{ color: "#D4B58C" }}
        data-testid="caul-price-ticker-empty"
      >
        <Activity size={11} strokeWidth={1.75} className="opacity-80" />
        <span>CAUL price syncing…</span>
      </div>
    );
  }

  const xrp = Number(price.quotePrice);
  // Use live XRP/USD from the oracle (CoinGecko-sourced) and the live
  // CAUL/USD it computed — these match what XMagnetic shows. Only fall
  // back to a static rate if the oracle didn't include USD info.
  const usd = price.usdPrice != null
    ? Number(price.usdPrice)
    : xrp * (price.xrpUsd != null ? Number(price.xrpUsd) : 0.5);
  // Format very small numbers with subscript-style truncation
  const xrpStr = xrp < 0.0001 ? xrp.toExponential(2) : xrp.toFixed(6);
  const usdStr = usd < 0.0001 ? `<$0.0001` : `$${usd.toFixed(4)}`;

  const Icon = trend === "up" ? TrendingUp : trend === "down" ? TrendingDown : Activity;
  // Explicit brand-palette colors that have proven contrast against the
  // dark navy app background (#15102A). text-secondary in the root theme
  // resolves to violet which fades; we override to champagne gold + ember
  // primary which match the rest of the header chrome.
  const GOLD = "#D4B58C";        // champagne — matches "TREASURY" label
  const EMBER = "#D95C14";       // primary orange — for down trends
  const EMERALD = "#5DD39E";     // bright moss — for up trends
  const tone = price.stale
    ? "border-[#D4B58C]/60 bg-[#D4B58C]/[0.10]"
    : trend === "up"
      ? "border-[#5DD39E]/60 bg-[#5DD39E]/[0.10]"
      : trend === "down"
        ? "border-[#D95C14]/60 bg-[#D95C14]/[0.10]"
        : "border-[#D4B58C]/50 bg-[#D4B58C]/[0.08]";
  const textColor = price.stale || trend === "flat"
    ? GOLD
    : trend === "up"
      ? EMERALD
      : EMBER;

  const updatedAgo = price.updatedAt
    ? relativeAgo(new Date(price.updatedAt).getTime())
    : null;

  return (
    <div
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-[11px] transition-colors ${tone} ${className}`}
      style={{ color: textColor }}
      data-testid="caul-price-ticker"
      title={
        price.stale
          ? "Live DEX order book is empty — showing last known print"
          : `Bid ${price.bid ?? "?"} · Ask ${price.ask ?? "?"} XRP`
      }
    >
      {price.stale ? (
        <AlertTriangle size={11} strokeWidth={1.75} />
      ) : (
        <Icon size={11} strokeWidth={1.75} />
      )}
      <span className="font-mono tabular-nums font-medium" data-testid="caul-price-ticker-xrp">
        1 CAUL ≈ {xrpStr} XRP
      </span>
      <span className="opacity-50">·</span>
      <span className="font-mono tabular-nums font-medium" data-testid="caul-price-ticker-usd">
        {usdStr}
      </span>
      {price.stale && (
        <span className="uppercase tracking-[0.18em] text-[9px] ml-1 px-1.5 py-0.5 rounded-sm bg-secondary/20 font-semibold" data-testid="caul-price-ticker-stale">
          stale
        </span>
      )}
      {updatedAgo && (
        <span className="opacity-70 text-[10px] ml-1" data-testid="caul-price-ticker-ago">
          · {updatedAgo}
        </span>
      )}
    </div>
  );
}

function relativeAgo(then) {
  const diff = Math.max(0, Date.now() - then);
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}
